// Auth.js - Cookie-based authentication (XSS-safe)
// Cookies are now set by the backend via httpOnly cookie, not stored in JS
// This file provides backwards-compatible interface for existing code

const API_BASE = process.env.REACT_APP_API_URL || '';

// Track authentication state in memory (not localStorage)
let isAuthenticated = false;
let currentUser = null;

// For backwards compatibility - no longer stores in localStorage
// The token is now managed via httpOnly cookies set by the server
export function setToken(t) {
  // We no longer store the token in localStorage (XSS vulnerability)
  // The httpOnly cookie is set by the backend automatically
  // This function now just marks us as authenticated
  isAuthenticated = true;
}

// No longer returns the actual token (it's in httpOnly cookie)
// This is kept for backwards compatibility but returns null
// All API calls should use credentials: 'include' to send cookies
export function getToken() {
  // Return null - we don't expose the token to JavaScript anymore
  // The cookie is sent automatically with credentials: 'include'
  return null;
}

export function clearToken() {
  isAuthenticated = false;
  currentUser = null;
  // Clear any legacy localStorage token
  try { localStorage.removeItem("token"); } catch {}
}

// Check if user is authenticated by calling /auth/me
export async function checkAuth() {
  try {
    const res = await fetch(`${API_BASE}/auth/me`, {
      credentials: 'include' // Send cookies
    });
    if (res.ok) {
      const data = await res.json();
      if (data.ok && data.user) {
        isAuthenticated = true;
        currentUser = data.user;
        return { authenticated: true, user: data.user };
      }
    }
  } catch (e) {
    console.error('Auth check failed:', e);
  }
  isAuthenticated = false;
  currentUser = null;
  return { authenticated: false, user: null };
}

// Logout - calls backend to clear cookie
export async function logout() {
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
  } catch (e) {
    console.error('Logout failed:', e);
  }
  clearToken();
}

// Get current authentication state
export function isLoggedIn() {
  return isAuthenticated;
}

export function getCurrentUser() {
  return currentUser;
}

export function setCurrentUser(user) {
  currentUser = user;
  isAuthenticated = !!user;
}

// Global 401 handler - redirects to login on session expiry
let on401Handler = null;

export function setOn401Handler(handler) {
  on401Handler = handler;
}

// Authenticated fetch wrapper - automatically handles 401 responses
export async function authFetch(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    credentials: 'include'
  });

  // Handle 401 - session expired or not authenticated
  if (res.status === 401) {
    console.warn('[Auth] Session expired or unauthorized - redirecting to login');
    clearToken();

    // Call custom handler if set (from AuthContext)
    if (on401Handler) {
      on401Handler();
    } else {
      // Fallback: redirect directly
      window.location.href = '/login?expired=1';
    }

    // Return a rejected promise to stop further processing
    throw new Error('Session expired');
  }

  return res;
}
